import type { ApolloClient } from '@apollo/client';
import type { Education } from '@seek/ca-myprofile-utils';

import { verifiableCredentialTypenameToCertsyType } from 'src/components/Profile/Verifications/analyticsUtils';
import type {
  Verifiable,
  VerifiableCredential,
  Verified,
} from 'src/components/Profile/Verifications/types';
import {
  PROFILE_PAGE,
  CREATE_RESUME_PAGE,
  CREATE_RESUME_PAGE_URL_REGEX,
  PROFILE_PAGE_URL_REGEX,
} from 'src/components/Router/index.ts';
import type { VerificationStatus } from 'src/modules/analytics/types';
import { CredentialExpiryStatus, type Licence } from 'src/types/graphql';

import { Actions as CareerHistoryActions } from '../components/Profile/CareerHistory/actions.ts';
import { Actions as EducationActions } from '../components/Profile/Education/actions.ts';
import { Actions as LanguageActions } from '../components/Profile/Languages/actions.ts';
import { Actions as LicenceActions } from '../components/Profile/Licences/actions.ts';
import { Actions as NextRoleActions } from '../components/Profile/NextRole/reducer.ts';
import { Actions as PersonalDetailsActions } from '../components/Profile/PersonalDetails/actions.ts';
import { Actions as ProfileVisibilityActions } from '../components/Profile/ProfileVisibility/actions.ts';
import { Actions as ResumeActions } from '../components/Profile/Resume/reducer.ts';
import { Actions as SkillsActions } from '../components/Profile/Skills/actions.ts';
import { Actions as SummaryActions } from '../components/Profile/Summary/actions.ts';
import { getCandidateIdFromCache } from '../components/Profile/data.ts';
import type { AppConfig } from '../modules/appConfig/types.ts';
import { checkProfileSuggestionsExperiment } from '../modules/features/profileSuggestionsAbTest.ts';
import type { Page } from '../types';

export const calculateLicenceAnalytics = (
  licences: Array<Partial<Pick<Licence, 'credential' | 'verificationUrl'>>>,
) => {
  // Verified licences have credentials
  // Not verified licences do not have credentials but have a verification URL
  // Unverifiable licences do not have credentials or a verification URL
  // Verified expired licences have an `Expired` status in the credential

  // Not verified licences and unverifiable licences are mutually exclusive

  const licencesNoCredentials = licences.filter(
    (licence) => !Boolean(licence.credential),
  );
  const licencesNoVerificationUrl = licencesNoCredentials.filter(
    (licence) => !Boolean(licence.verificationUrl),
  );
  const verifiedExpiredLicences = licences.filter(
    (licence) =>
      licence?.credential?.expiryStatus === CredentialExpiryStatus.Expired,
  );

  return {
    licencesVerifiedTotal: licences.length - licencesNoCredentials.length,
    licencesNotVerifiedTotal:
      licencesNoCredentials.length - licencesNoVerificationUrl.length,
    licencesUnverifiableTotal: licencesNoVerificationUrl.length,
    licencesExpiredVerifiedTotal: verifiedExpiredLicences.length,
  };
};

export const calculateQualificationAnalytics = (
  qualifications: Education[],
) => {
  const qualificationsNoCredentials = qualifications.filter(
    (qualification) => !Boolean(qualification.credential),
  );
  const qualificationsNoVerificationUrl = qualificationsNoCredentials.filter(
    (qualification) => !Boolean(qualification.verificationUrl),
  );

  return {
    qualificationsVerifiedTotal:
      qualifications.length - qualificationsNoCredentials.length,
    qualificationsNotVerifiedTotal:
      qualificationsNoCredentials.length -
      qualificationsNoVerificationUrl.length,
    qualificationsUnverifiableTotal: qualificationsNoVerificationUrl.length,
  };
};

export const getVerificationStatus = (
  verification: Pick<Licence | Education, 'credential' | 'verificationUrl'>,
): VerificationStatus => {
  if (verification?.credential) {
    return 'verified';
  }

  if (verification?.verificationUrl) {
    return 'not-verified';
  }

  return 'unverifiable';
};

/**
 * Used to filter out expired or expiring credentials
 * so it doesn't count towards "credentialsNotVerifiedTotal" and "credentialsNotVerifiedType" tracking props
 */
export const isNotVerifiedCredential = (credential: VerifiableCredential) =>
  !(
    'expiryStatus' in credential &&
    (credential.expiryStatus === CredentialExpiryStatus.Expired ||
      credential.expiryStatus === CredentialExpiryStatus.ExpiringSoon)
  );

export const calculateVerificationsAnalytics = (
  verified: Verified | null | undefined,
  verifiable: Verifiable | null | undefined,
) => {
  let credentialsVerifiedTotal;
  let credentialsVerifiedType;
  let credentialsNotVerifiedTotal;
  let credentialsNotVerifiedType;

  if (verified) {
    const verifiedCredentials = verified.flatMap(
      (verification) => verification.credentials,
    );

    credentialsVerifiedTotal = verifiedCredentials.length;
    const verifiedCredentialUniqueTypes = [
      ...new Set(
        verifiedCredentials.map((credential) => credential?.credentialType),
      ),
    ];
    credentialsVerifiedType = verifiedCredentialUniqueTypes.sort().join(',');
  }

  if (verifiable) {
    const verifiableCredentials = verifiable
      .flatMap((credential) => credential.credentials)
      .filter((credential) => credential !== null);

    const credentialsNotVerified = verifiableCredentials.filter(
      isNotVerifiedCredential,
    );

    credentialsNotVerifiedTotal = credentialsNotVerified.length;
    const verifiableCredentialUniqueTypes = [
      ...new Set(
        credentialsNotVerified
          .map((credential) =>
            credential && '__typename' in credential
              ? verifiableCredentialTypenameToCertsyType[credential?.__typename]
              : null,
          )
          .filter((type) => type !== null),
      ),
    ];
    credentialsNotVerifiedType = verifiableCredentialUniqueTypes
      .sort()
      .join(',');
  }

  return {
    credentialsVerifiedTotal,
    credentialsVerifiedType,
    credentialsNotVerifiedTotal,
    credentialsNotVerifiedType,
  };
};

const apiUpdates: string[] = [
  PersonalDetailsActions.PERSONAL_DETAILS_UPDATE_SUCCESS,
  SummaryActions.SUMMARY_UPDATE_SUCCESS,
  CareerHistoryActions.UPDATE_CAREER_HISTORY_SUCCESS,
  CareerHistoryActions.DELETE_CAREER_HISTORY_SUCCESS,
  SkillsActions.UPDATE_SKILLS_SUCCESS,
  ResumeActions.RESUME_UPLOAD_SUCCESS,
  ResumeActions.RESUME_DELETE_SUCCESS,
  ResumeActions.RESUME_UPDATE_DEFAULT_SUCCESS,
  ProfileVisibilityActions.PROFILE_VISIBILITY_UPDATE_SUCCESS,
  NextRoleActions.NEXT_ROLE_UPDATE_WORKTYPE_SUCCESS,
  NextRoleActions.NEXT_ROLE_UPDATE_AVAILABILITY_SUCCESS,
  NextRoleActions.NEXT_ROLE_UPDATE_CLASSIFICATION_SUCCESS,
  EducationActions.UPDATE_QUALIFICATION_SUCCESS,
  EducationActions.REMOVE_QUALIFICATION_SUCCESS,
  NextRoleActions.NEXT_ROLE_UPDATE_SALARY_SUCCESS,
  NextRoleActions.NEXT_ROLE_UPDATE_RTW_SUCCESS,
  LicenceActions.UPDATE_LICENCE_SUCCESS,
  LicenceActions.REMOVE_LICENCE_SUCCESS,
  LanguageActions.UPDATE_LANGUAGE_SUCCESS,
  LanguageActions.REMOVE_LANGUAGE_SUCCESS,
];

// Get page without usePage hook - for legacy class based components.
export const getCurrentPage = (): Page => {
  const path = window.location.pathname;

  if (CREATE_RESUME_PAGE_URL_REGEX.test(path)) {
    return CREATE_RESUME_PAGE;
  }

  if (PROFILE_PAGE_URL_REGEX.test(path)) {
    return PROFILE_PAGE;
  }

  return PROFILE_PAGE; // No matching route
};

// TODO: Used in Tips & Suggestions updaters. Will be deprecated in a future PR
export const shouldUpdate = (
  type: string,
  appConfig: AppConfig,
  apolloClient: ApolloClient<any>,
) => {
  const { isInProfileSuggestionsTestGroup } = checkProfileSuggestionsExperiment(
    {
      zone: appConfig.zone,
      page: getCurrentPage(),
      candidateId: getCandidateIdFromCache(apolloClient.cache),
    },
  );

  return apiUpdates.includes(type) && !isInProfileSuggestionsTestGroup;
};
